import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaExchangeAlt, FaSearch } from 'react-icons/fa';
import { compareAuditLogs, getAuditLogs } from '../../../redux/Slices/AuditLogSlice';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../shared/hooks/TitleContext';
import i18n from '../../../i18n/i18n';

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const CompareAuditLogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [logId1, setLogId1] = useState('');
  const [logId2, setLogId2] = useState('');
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logDropdown1Open, setLogDropdown1Open] = useState(false);
  const [logDropdown2Open, setLogDropdown2Open] = useState(false);
  const [logSearchTerm, setLogSearchTerm] = useState('');
  const [logPage, setLogPage] = useState(1);
  const logSearchInputRef = useRef(null);

  useEffect(() => {
    setTitle('auditLogs.compareTitle', { defaultValue: 'مقارنة السجلات' });
  }, [setTitle]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.log-dropdown-container')) {
        setLogDropdown1Open(false);
        setLogDropdown2Open(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAuditLogs = async (page = logPage, search = logSearchTerm) => {
    setIsLoadingLogs(true);
    try {
      const params = { page, per_page: 50 };
      if (search) {
        // Search by ID, model type, model ID, or event
        params.search = search;
      }
      const result = await dispatch(getAuditLogs(params));
      if (getAuditLogs.fulfilled.match(result)) {
        const { items, pagination: paginationData } = normalizeListResponse(result.payload);
        setAuditLogs(items);
        setPagination(paginationData);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (logDropdown1Open || logDropdown2Open) {
      fetchAuditLogs(logPage, logSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logPage, logDropdown1Open, logDropdown2Open]);

  useEffect(() => {
    if (logSearchTerm !== undefined && (logDropdown1Open || logDropdown2Open)) {
      const timeoutId = setTimeout(() => {
        setLogPage(1);
        fetchAuditLogs(1, logSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logSearchTerm, logDropdown1Open, logDropdown2Open]);

  const handleCompare = async () => {
    if (!logId1 || !logId2) {
      toast.error(t('auditLogs.errors.selectTwoLogs', { defaultValue: 'يرجى اختيار سجلين للمقارنة' }), { rtl: isRTL });
      return;
    }

    if (logId1 === logId2) {
      toast.error(t('auditLogs.errors.sameLogs', { defaultValue: 'يرجى اختيار سجلين مختلفين' }), { rtl: isRTL });
      return;
    }

    setIsComparing(true);
    try {
      const result = await dispatch(compareAuditLogs({
        logId1: parseInt(logId1),
        logId2: parseInt(logId2),
        language: i18n.language
      }));
      if (compareAuditLogs.fulfilled.match(result)) {
        const data = result.payload?.data || result.payload;
        setComparisonResult(data);
        toast.success(t('auditLogs.success.compared', { defaultValue: 'تمت المقارنة بنجاح' }), { rtl: isRTL });
      } else {
        toast.error(result.payload || t('auditLogs.errors.compareError', { defaultValue: 'فشل في المقارنة' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(t('auditLogs.errors.compareError', { defaultValue: 'فشل في المقارنة' }), { rtl: isRTL });
    } finally {
      setIsComparing(false);
    }
  };

  const formatJson = (obj) => {
    if (!obj) return '—';
    if (typeof obj === 'string') {
      try {
        return JSON.stringify(JSON.parse(obj), null, 2);
      } catch {
        return obj;
      }
    }
    return JSON.stringify(obj, null, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEventLabel = (eventType) => {
    const labels = {
      created: t('auditLogs.events.created', { defaultValue: 'Created' }),
      updated: t('auditLogs.events.updated', { defaultValue: 'Updated' }),
      deleted: t('auditLogs.events.deleted', { defaultValue: 'Deleted' }),
      restored: t('auditLogs.events.restored', { defaultValue: 'Restored' }),
    };
    return labels[eventType] || eventType;
  };

  return (
    <div style={{ padding: '20px', direction: dir, textAlign }}>
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #333'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: 'white', margin: 0 }}>
            {t('auditLogs.compareTitle', { defaultValue: 'مقارنة السجلات' })}
          </h2>
          <button
            onClick={() => navigate('/audit-logs')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaArrowLeft /> {t('auditLogs.back', { defaultValue: 'رجوع' })}
          </button>
        </div>

        <div style={{
          backgroundColor: '#1a1f2e',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            <div style={{ position: 'relative' }} className="log-dropdown-container">
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.logId1', { defaultValue: 'اختر السجل الأول' })}
              </label>
              <div
                onClick={() => {
                  setLogDropdown1Open(!logDropdown1Open);
                  setLogDropdown2Open(false);
                  if (!logDropdown1Open) {
                    fetchAuditLogs(1, logSearchTerm);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>
                  {logId1 ? `#${logId1} - ${auditLogs.find(l => String(l.id) === String(logId1))?.auditable_type?.replace(/App\\Models\\/g, '') || '—'} #${auditLogs.find(l => String(l.id) === String(logId1))?.auditable_id || '—'}` : t('auditLogs.selectLog', { defaultValue: 'اختر سجل' })}
                </span>
                <span>▼</span>
              </div>
              {logDropdown1Open && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #333' }}>
                    <input
                      ref={logSearchInputRef}
                      type="text"
                      placeholder={t('common.search', { defaultValue: isRTL ? 'بحث...' : 'Search...' })}
                      value={logSearchTerm}
                      onChange={(e) => setLogSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: '#0d1117',
                        border: 'none',
                        borderBottom: '1px solid #333',
                        color: 'white',
                        borderRadius: '6px 6px 0 0'
                      }}
                    />
                  </div>
                  {isLoadingLogs ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
                      {t('auditLogs.loading', { defaultValue: 'جاري التحميل...' })}
                    </div>
                  ) : auditLogs.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
                      {t('auditLogs.noData', { defaultValue: 'لا توجد بيانات' })}
                    </div>
                  ) : (
                    <>
                      {auditLogs.map((log) => (
                        <div
                          key={log.id}
                          onClick={() => {
                            setLogId1(String(log.id));
                            setLogDropdown1Open(false);
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #333',
                            color: String(logId1) === String(log.id) ? '#AC2000' : 'white',
                            backgroundColor: String(logId1) === String(log.id) ? '#2a2a2a' : 'transparent'
                          }}
                        >
                          #{log.id} - {(log.auditable_type || log.model_type || '—').replace(/App\\Models\\/g, '')} #{log.auditable_id || log.model_id || '—'} - {getEventLabel(log.event)}
                        </div>
                      ))}
                      {pagination && pagination.last_page > 1 && (
                        <div style={{ padding: '10px', borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLogPage(prev => Math.max(1, prev - 1));
                            }}
                            disabled={logPage === 1}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: logPage === 1 ? '#666' : '#AC2000',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: logPage === 1 ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ‹
                          </button>
                          <span style={{ color: '#ccc', fontSize: '12px', flex: 1, textAlign: 'center' }}>
                            {t('auditLogs.page', { defaultValue: 'صفحة' })} {logPage} {t('auditLogs.of', { defaultValue: 'من' })} {pagination.last_page}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLogPage(prev => Math.min(pagination.last_page, prev + 1));
                            }}
                            disabled={logPage === pagination.last_page}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: logPage === pagination.last_page ? '#666' : '#AC2000',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: logPage === pagination.last_page ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ›
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <div style={{ position: 'relative' }} className="log-dropdown-container">
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.logId2', { defaultValue: 'اختر السجل الثاني' })}
              </label>
              <div
                onClick={() => {
                  setLogDropdown2Open(!logDropdown2Open);
                  setLogDropdown1Open(false);
                  if (!logDropdown2Open) {
                    fetchAuditLogs(1, logSearchTerm);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>
                  {logId2 ? `#${logId2} - ${auditLogs.find(l => String(l.id) === String(logId2))?.auditable_type?.replace(/App\\Models\\/g, '') || '—'} #${auditLogs.find(l => String(l.id) === String(logId2))?.auditable_id || '—'}` : t('auditLogs.selectLog', { defaultValue: 'اختر سجل' })}
                </span>
                <span>▼</span>
              </div>
              {logDropdown2Open && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #333' }}>
                    <input
                      ref={logSearchInputRef}
                      type="text"
                      placeholder={t('common.search', { defaultValue: isRTL ? 'بحث...' : 'Search...' })}
                      value={logSearchTerm}
                      onChange={(e) => setLogSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: '#0d1117',
                        border: 'none',
                        borderBottom: '1px solid #333',
                        color: 'white',
                        borderRadius: '6px 6px 0 0'
                      }}
                    />
                  </div>
                  {isLoadingLogs ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
                      {t('auditLogs.loading', { defaultValue: 'جاري التحميل...' })}
                    </div>
                  ) : auditLogs.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
                      {t('auditLogs.noData', { defaultValue: 'لا توجد بيانات' })}
                    </div>
                  ) : (
                    <>
                      {auditLogs.map((log) => (
                        <div
                          key={log.id}
                          onClick={() => {
                            setLogId2(String(log.id));
                            setLogDropdown2Open(false);
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #333',
                            color: String(logId2) === String(log.id) ? '#AC2000' : 'white',
                            backgroundColor: String(logId2) === String(log.id) ? '#2a2a2a' : 'transparent'
                          }}
                        >
                          #{log.id} - {(log.auditable_type || log.model_type || '—').replace(/App\\Models\\/g, '')} #{log.auditable_id || log.model_id || '—'} - {getEventLabel(log.event)}
                        </div>
                      ))}
                      {pagination && pagination.last_page > 1 && (
                        <div style={{ padding: '10px', borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLogPage(prev => Math.max(1, prev - 1));
                            }}
                            disabled={logPage === 1}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: logPage === 1 ? '#666' : '#AC2000',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: logPage === 1 ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ‹
                          </button>
                          <span style={{ color: '#ccc', fontSize: '12px', flex: 1, textAlign: 'center' }}>
                            {t('auditLogs.page', { defaultValue: 'صفحة' })} {logPage} {t('auditLogs.of', { defaultValue: 'من' })} {pagination.last_page}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLogPage(prev => Math.min(pagination.last_page, prev + 1));
                            }}
                            disabled={logPage === pagination.last_page}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: logPage === pagination.last_page ? '#666' : '#AC2000',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: logPage === pagination.last_page ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ›
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={handleCompare}
              disabled={isComparing || !logId1 || !logId2}
              style={{
                backgroundColor: (isComparing || !logId1 || !logId2) ? '#666' : '#fd7e14',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: (isComparing || !logId1 || !logId2) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px'
              }}
            >
              <FaExchangeAlt /> {isComparing ? t('auditLogs.comparing', { defaultValue: 'جاري المقارنة...' }) : t('auditLogs.compare', { defaultValue: 'مقارنة' })}
            </button>
          </div>
        </div>

        {comparisonResult && (
          <div style={{
            backgroundColor: '#1a1f2e',
            padding: '20px',
            borderRadius: '8px'
          }}>
            <h3 style={{ color: 'white', marginBottom: '20px' }}>
              {t('auditLogs.comparisonResult', { defaultValue: 'نتيجة المقارنة' })}
            </h3>

            {/* Log Information */}
            {comparisonResult.log_1 && comparisonResult.log_2 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div style={{ backgroundColor: '#14263b', padding: '15px', borderRadius: '8px' }}>
                    <h4 style={{ color: '#51cf66', marginBottom: '10px' }}>
                      {t('auditLogs.log1', { defaultValue: 'السجل الأول' })} #{comparisonResult.log_1.id}
                    </h4>
                    <div style={{ color: '#ccc', fontSize: '14px' }}>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{t('auditLogs.event', { defaultValue: 'الحدث' })}:</strong> {getEventLabel(comparisonResult.log_1.event)}
                      </div>
                      <div>
                        <strong>{t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })}:</strong> {formatDate(comparisonResult.log_1.created_at)}
                      </div>
                    </div>
                  </div>
                  <div style={{ backgroundColor: '#14263b', padding: '15px', borderRadius: '8px' }}>
                    <h4 style={{ color: '#ff6b6b', marginBottom: '10px' }}>
                      {t('auditLogs.log2', { defaultValue: 'السجل الثاني' })} #{comparisonResult.log_2.id}
                    </h4>
                    <div style={{ color: '#ccc', fontSize: '14px' }}>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{t('auditLogs.event', { defaultValue: 'الحدث' })}:</strong> {getEventLabel(comparisonResult.log_2.event)}
                      </div>
                      <div>
                        <strong>{t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })}:</strong> {formatDate(comparisonResult.log_2.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Differences */}
            {comparisonResult.differences && Object.keys(comparisonResult.differences).length > 0 ? (
              <div>
                <h4 style={{ color: 'white', marginBottom: '15px' }}>
                  {t('auditLogs.differences', { defaultValue: 'الاختلافات' })}
                </h4>
                <div style={{ backgroundColor: '#14263b', padding: '15px', borderRadius: '8px' }}>
                  {Object.entries(comparisonResult.differences).map(([key, diff]) => (
                    <div key={key} style={{
                      marginBottom: '15px',
                      padding: '12px',
                      backgroundColor: '#0d1117',
                      borderRadius: '6px',
                      border: '1px solid #333'
                    }}>
                      <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: '10px', fontSize: '16px' }}>
                        {key}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                          <div style={{ color: '#51cf66', fontSize: '12px', marginBottom: '5px' }}>
                            {t('auditLogs.version1', { defaultValue: 'الإصدار الأول' })}
                          </div>
                          <div style={{
                            color: '#fff',
                            padding: '8px',
                            backgroundColor: '#1a1f2e',
                            borderRadius: '4px',
                            wordBreak: 'break-word'
                          }}>
                            {diff.version_1 !== null && diff.version_1 !== undefined
                              ? (typeof diff.version_1 === 'object' ? JSON.stringify(diff.version_1, null, 2) : String(diff.version_1))
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#ff6b6b', fontSize: '12px', marginBottom: '5px' }}>
                            {t('auditLogs.version2', { defaultValue: 'الإصدار الثاني' })}
                          </div>
                          <div style={{
                            color: '#fff',
                            padding: '8px',
                            backgroundColor: '#1a1f2e',
                            borderRadius: '4px',
                            wordBreak: 'break-word'
                          }}>
                            {diff.version_2 !== null && diff.version_2 !== undefined
                              ? (typeof diff.version_2 === 'object' ? JSON.stringify(diff.version_2, null, 2) : String(diff.version_2))
                              : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                backgroundColor: '#14263b',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#51cf66'
              }}>
                {t('auditLogs.noDifferences', { defaultValue: 'لا توجد اختلافات بين السجلين' })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareAuditLogs;
